<template>
  <AwModal :open="open" size="md" position="center" @update:open="emit('update:open', $event)">
    <template #header="{ close }">
      <div class="flex items-center gap-2 w-full">
        <AwIcon name="i-ph-money" class="size-4" />
        <h2 class="font-500 text-md">Demande de couverture</h2>
        <AwButton icon="i-ph-x-bold" size="sm" color="secondary" variant="ghost" class="ml-auto" @click="close()" />
      </div>
    </template>

    <template #body>
      <div class="flex flex-col gap-4 px-6 py-4">
        <AwBanner v-if="errorMessage" color="critical" icon="i-ph-seal-warning-fill" :text="errorMessage" />
        <AwBanner icon="i-ph-info" :text="currentCoverLabel" />
        <AwFormField
          label="Montant total à couvrir"
          required
          :help="helpText"
          :error-message="amountError ?? undefined"
          class="w-full"
        >
          <AwTextField v-model="amount" type="number" placeholder="Ex. 40 000" class="w-full">
            <template #trailing>
              <span class="text-secondary-700">€</span>
            </template>
          </AwTextField>
        </AwFormField>
        <AwBanner v-if="isBelowMinimum" color="primary" icon="i-ph-info" :text="minimumAppliedText" />
      </div>
    </template>

    <template #footer="{ close }">
      <div class="flex items-center justify-end gap-3 w-full">
        <AwButton color="secondary" variant="outline" :disabled="isSubmitting" @click="close()">Annuler</AwButton>
        <AwButton
          color="primary"
          variant="solid"
          label="Confirmer la demande"
          :is-loading="isSubmitting"
          :disabled="!isAmountValid"
          @click="confirm(close)"
        />
      </div>
    </template>
  </AwModal>
</template>

<script setup lang="ts">
import { useToast } from "~/composables/useToast";
import { AwBanner, AwButton, AwFormField, AwIcon, AwModal, AwTextField } from "@asap/design";
import { currencyFormat } from "~/utils/currency";
import { useCreditInsurerRequest } from "~/components/company/composables/useCreditInsurerRequest";
import { useCoverageTracking, type CoverageSource } from "~/components/company/composables/useCoverageTracking";

// Sum of the company's approved covers, in EUR (0 when none).
const props = defineProps<{ open: boolean; companyId: string; currentCoveredAmount: number; source: CoverageSource }>();
const emit = defineEmits<{ "update:open": [boolean]; created: [] }>();

const { requestCover } = useCreditInsurerRequest();
const { addToastSuccess } = useToast();
const coverageTracking = useCoverageTracking();

const amount = ref<number | null>(null);
const isSubmitting = ref(false);
const errorMessage = ref<string | null>(null);

// Mirrors the API rules: below the minimum an ND cover is requested, which covers up to the
// minimum; above the cap the request is rejected.
const MINIMUM_COVER_AMOUNT_EUR = 20_000;
const MAXIMUM_COVER_AMOUNT_EUR = 200_000;

const formatEur = (value: number) => currencyFormat(value, { options: { maximumFractionDigits: 0 } });

const currentCoverLabel = computed(() =>
  props.currentCoveredAmount > 0
    ? `Couverture actuelle : ${formatEur(props.currentCoveredAmount)}`
    : "Aucune couverture active"
);
const helpText = `Indique le montant total nécessaire, couverture actuelle comprise. Minimum : ${formatEur(MINIMUM_COVER_AMOUNT_EUR)}. Maximum : ${formatEur(MAXIMUM_COVER_AMOUNT_EUR)}.`;
const minimumAppliedText = `La demande sera envoyée pour ${formatEur(MINIMUM_COVER_AMOUNT_EUR)}, le montant minimum.`;

// The API requires a positive integer amount (EUR). The number input emits a number (or null).
const isPositiveInteger = computed(
  () => typeof amount.value === "number" && Number.isInteger(amount.value) && amount.value > 0
);

const amountError = computed(() => {
  if (!isPositiveInteger.value || amount.value === null) return null;
  if (props.currentCoveredAmount > 0 && amount.value <= props.currentCoveredAmount) {
    return `Le montant doit être supérieur à la couverture actuelle (${formatEur(props.currentCoveredAmount)}).`;
  }
  if (amount.value > MAXIMUM_COVER_AMOUNT_EUR) {
    return `Le montant ne peut pas dépasser ${formatEur(MAXIMUM_COVER_AMOUNT_EUR)}.`;
  }
  return null;
});

const isAmountValid = computed(() => isPositiveInteger.value && amountError.value === null);

const isBelowMinimum = computed(
  () => isAmountValid.value && amount.value !== null && amount.value < MINIMUM_COVER_AMOUNT_EUR
);

// Reset the field + error each time the modal opens.
watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      amount.value = null;
      errorMessage.value = null;
      coverageTracking.trackRequestModalOpened(props.companyId, props.source);
    }
  }
);

const confirm = async (close: () => void) => {
  if (!isAmountValid.value || amount.value === null) return;

  isSubmitting.value = true;
  errorMessage.value = null;
  try {
    await requestCover(props.companyId, amount.value);
    coverageTracking.trackRequestSubmitted(props.companyId, props.source, amount.value);
    addToastSuccess({ title: "Demande envoyée à Allianz." });
    emit("created");
    close();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "Échec de l'envoi de la demande. Réessaie.";
    coverageTracking.trackRequestFailed(props.companyId, props.source, errorMessage.value);
  } finally {
    isSubmitting.value = false;
  }
};
</script>
